<template>
  <paginated-table
    id="admin-libraries-table"
    ref="table"
    endpoint="libraries"
    :fetch-params="{
      for: 'admin',
    }"
    :label="$t('titles.libraries')"
    :columns="columns"
    :extra-filters="extraFilters"
    :show-generate-csv="false"
    sync-filters-with-url
    row-link
  >
    <template #head-buttons>
      <icon-button v-if="canCreateLibrary" to="/admin/libraries/new" role="add">
        {{ $t("views.admin.libraries.actions.create") }}
      </icon-button>
    </template>
    <template #cell(actions)="{ item }">
      <validated-button
        v-if="canDeleteLibrary"
        :action="async () => deleteLibrary(item)"
        :question="$t('views.admin.libraries.delete_confirm', { name: item.name })"
        :label="$t('forms.remove') | capitalize"
        icon="trash"
        size="sm"
        variant="ghost-danger"
        :show-button-label="false"
      />
    </template>
  </paginated-table>
</template>

<script>
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";
import { capitalize } from "@/helpers/filters";
import { userLink } from "@/helpers/links";
import { getCommunityAdminFilter } from "@/helpers/permissions/communities";
import { isGlobalAdmin } from "@/helpers/permissions/users";
import { del } from "@/requests/server";

export default {
  name: "AdminLibraries",
  components: {
    ValidatedButton,
    IconButton,
    PaginatedTable,
  },
  computed: {
    extraFilters() {
      return [
        new Filter(
          "sharedInCommunity",
          capitalize(this.$t("communities.fields.community.name")),
          "relation",
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read", "id") },
          }
        ),
      ];
    },
    columns() {
      return [
        new Column("id", capitalize(this.$t("lists.id")), "id"),
        new Column("name", capitalize(this.$t("views.admin.libraries.columns.name")), "text"),
        Column.withRelation(
          "owner_user.full_name",
          this.$t("views.admin.libraries.columns.owner"),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { ...getCommunityAdminFilter(this.$store.state.user, "read") },
          },
          "owner_user_id",
          {
            urlFct: (library) => userLink(library.owner_user),
            sortable: false,
          }
        ),
        Column.withRelationCount(
          "communities_count",
          this.$t("views.admin.libraries.columns.communities_count")
        ),
        Column.withRelationCount(
          "loanables_count",
          this.$t("views.admin.libraries.columns.loanables_count")
        ),
      ];
    },
    isGlobalAdmin() {
      return isGlobalAdmin(this.$store.state.user);
    },
    canCreateLibrary() {
      return this.isGlobalAdmin;
    },
    canDeleteLibrary() {
      return this.isGlobalAdmin;
    },
  },
  methods: {
    async deleteLibrary(library) {
      await del(`/libraries/${library.id}`, {
        notifications: {
          action: this.$t("views.admin.libraries.notifications.delete.action"),
          resourceName: this.$t("views.admin.libraries.notifications.delete.resource"),
          onSuccess: true,
        },
      });
      this.$refs.table.refresh();
    },
  },
};
</script>

<style lang="scss"></style>
